/**
 * LiteLLM Client — Non-streaming browser-to-LiteLLM proxy calls
 *
 * Calls the LiteLLM gateway (chat.floorplanner.com/llm) directly from
 * the browser using JWT auth. OpenAI-compatible chat completions API.
 *
 * Ported from linda-editor/src/lib/litellm-client.ts (non-streaming only).
 */

import {logger} from '@/lib/logger';
import {getLiteLLMApiKey} from '@/lib/linda-jwt';

const log = logger.tag('LiteLLM');

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

// baseUrl is the gateway mount point (no /v1). Endpoint paths in this file
// include /v1/ explicitly so the full URL is /llm/v1/chat/completions.
const LITELLM_CONFIG = {
    baseUrl:
        (import.meta.env.VITE_LITELLM_BASE_URL as string | undefined) ??
        'https://chat.floorplanner.com/llm',
};

/** Model aliases configured in the LiteLLM gateway */
export const LITELLM_MODELS = {
    sonnet: 'claude-sonnet',
    haiku: 'claude-haiku',
} as const;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ChatMessage {
    role: 'system' | 'user' | 'assistant' | 'tool';
    content: string | null;
    tool_calls?: ToolCall[];
    tool_call_id?: string;
}

export interface Tool {
    type: 'function';
    function: {
        name: string;
        description: string;
        parameters: {
            type: 'object';
            properties: Record<string, unknown>;
            required?: string[];
        };
    };
}

export interface ToolCall {
    id: string;
    type: 'function';
    function: {
        name: string;
        arguments: string; // JSON string
    };
}

export interface ChatCompletionRequest {
    model: string;
    messages: ChatMessage[];
    max_tokens?: number;
    temperature?: number;
    tools?: Tool[];
    tool_choice?:
        | 'auto'
        | 'none'
        | 'required'
        | {type: 'function'; function: {name: string}};
    metadata?: Record<string, unknown>;
}

export interface ChatCompletionResponse {
    id: string;
    object: 'chat.completion';
    created: number;
    model: string;
    choices: Array<{
        index: number;
        message: {
            role: 'assistant';
            content: string | null;
            tool_calls?: ToolCall[];
        };
        finish_reason: 'stop' | 'length' | 'tool_calls';
    }>;
    usage: {
        prompt_tokens: number;
        completion_tokens: number;
        total_tokens: number;
    };
}

interface LiteLLMError {
    error: {
        message: string;
        type: string;
        code?: string;
    };
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

/**
 * Call LiteLLM chat completions API (non-streaming).
 */
export async function chatCompletion(
    request: ChatCompletionRequest
): Promise<ChatCompletionResponse> {
    const url = `${LITELLM_CONFIG.baseUrl}/v1/chat/completions`;
    const apiKey = await getLiteLLMApiKey();

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${apiKey}`,
            'X-Request-ID': `mb-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        },
        body: JSON.stringify({
            ...request,
            metadata: {
                app: 'modelbank-dashboard',
                ...request.metadata,
            },
        }),
    });

    if (!response.ok) {
        let errorMessage = `LiteLLM request failed: ${response.status}`;
        try {
            const errorData = (await response.json()) as LiteLLMError;
            errorMessage = errorData.error?.message ?? errorMessage;
        } catch {
            // Ignore JSON parse errors
        }
        log.warn('Chat completion failed', {
            httpStatus: String(response.status),
            model: request.model,
            errorMessage,
            userId: request.metadata?.user_id
                ? String(request.metadata.user_id)
                : undefined,
        });
        throw new Error(errorMessage);
    }

    return response.json() as Promise<ChatCompletionResponse>;
}
